import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {VoiceWave} from '@mia-platform-internal/chatbot-widget';

// Icons drawn here, in the colour of the box that holds them.
const draw = (children) => {
  const Svg = ({size = 22}) => (
    <svg aria-hidden="true" fill="none" height={size} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width={size}>{children}</svg>
  );
  Svg.propTypes = {size: PropTypes.number};
  return Svg;
};
const ChatIcon = draw(<path d="M4 5h16v11H9l-5 4V5z" />);
const ChartIcon = draw(<path d="M4 4v16h16M8 16v-4M12 16V8M16 16v-6" />);
const PlusIcon = draw(<path d="M12 5v14M5 12h14" />);
const FileIcon = draw(<path d="M7 3h7l4 4v14H7V3zM14 3v4h4" />);
const SearchIcon = draw(<path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4" />);
const GlobeIcon = draw(<path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3c2.5 2.5 3.5 5.5 3.5 9S14.5 18.5 12 21c-2.5-2.5-3.5-5.5-3.5-9S9.5 5.5 12 3z" />);
const SparkIcon = draw(<path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" />);
const TemplateIcon = draw(<path d="M4 4h16v6H4zM4 14h7v6H4zM15 14h5v6h-5z" />);
const MicIcon = draw(<><rect height="12" rx="3" width="6" x="9" y="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></>);
const CheckIcon = draw(<path d="M5 12l5 5 9-10" />);
const RefreshIcon = draw(<path d="M20 12a8 8 0 1 1-2.5-5.8M20 4v5h-5" />);
const BackIcon = draw(<path d="M15 6l-6 6 6 6" />);
const ArrowIcon = draw(<path d="M5 12h14M13 6l6 6-6 6" />);

// The three things a person comes to do. Each one has its own question and ONE row of choices — what to look at, or what to
// start from. The choice is put in front of the prompt (`prefix`), so the agent knows; some also open the file picker.
const MODES = {
  ask: {
    title: 'Ask & find',
    shortcut: 'F',
    blurb: 'Chat, search your documents, get answers',
    Icon: ChatIcon,
    heading: 'What would you like to know?',
    placeholder: 'Ask a question, or drop a document and ask about it...',
    options: [
      {key: 'chat', label: 'Just chat', Icon: ChatIcon, prefix: ''},
      {key: 'documents', label: 'My documents', Icon: FileIcon, prefix: 'Using my documents: ', attach: true},
      {key: 'web', label: 'Research online', Icon: GlobeIcon, prefix: 'Research online: '},
    ],
    next: 'Flow answers in the chat and shows where each answer comes from.',
  },
  create: {
    title: 'Create',
    shortcut: 'C',
    blurb: 'Brainstorm, then make charts, reports, analyses',
    Icon: ChartIcon,
    heading: 'What do you want to create?',
    placeholder: 'Describe the chart, report or analysis you want...',
    options: [
      {key: 'idea', label: 'Brainstorm an idea', Icon: SparkIcon, prefix: 'Brainstorm an idea with me: '},
      {key: 'report', label: 'A report from a document', Icon: FileIcon, prefix: 'Create a report from my document: ', attach: true},
      {key: 'spreadsheet', label: 'Analyze a spreadsheet', Icon: SearchIcon, prefix: 'Analyze this spreadsheet: ', attach: true},
    ],
    next: 'Flow brainstorms with you, then makes it and shows it in the chat.',
  },
  build: {
    title: 'Build an app',
    shortcut: 'A',
    blurb: 'Turn an idea into a working app',
    Icon: PlusIcon,
    heading: 'What should we build?',
    placeholder: 'Describe the app you want...',
    options: [
      {key: 'scratch', label: 'From scratch', Icon: PlusIcon, prefix: 'Create a new project: '},
      {key: 'fullstack', label: 'A full-stack app (front end, back end, database)', Icon: TemplateIcon, prefix: 'Create a new project, a full-stack app with front end, back end and database: '},
      {key: 'tool', label: 'An internal tool', Icon: ChartIcon, prefix: 'Create a new project, an internal tool: '},
      {key: 'landing', label: 'A landing page', Icon: FileIcon, prefix: 'Create a new project, a landing page: '},
    ],
    next: 'Flow builds it and shows you a live preview as it goes.',
  },
};

// T tells Flow by voice instead of picking a card.
const TELL_SHORTCUT = 'T';

const STEPS = ['Choose', 'Describe', 'Flow works'];

const Stepper = ({current}) => (
  <ol aria-label="Steps" className="hs-steps">
    {STEPS.map((label, index) => {
      const number = index + 1;
      const state = number < current ? 'done' : number === current ? 'current' : 'todo';
      return (
        <li aria-current={state === 'current' ? 'step' : undefined} className={`hs-step hs-step-${state}`} key={label}>
          <span className="hs-step-dot">{state === 'done' ? <CheckIcon size={14} /> : number}</span>
          <span className="hs-step-label">{label}</span>
        </li>
      );
    })}
  </ol>
);
Stepper.propTypes = {current: PropTypes.number.isRequired};

/**
 * The simple view of Home: three big choices, then one question, then Flow works. For someone who does not want to read a
 * dashboard — what is recent is tucked away behind "Show the full home".
 */
const HomeSimple = ({
  continueSession,
  firstName,
  formatRelativeTime,
  liveNow = null,
  onAttach,
  onFocusPrompt,
  onMode,
  onPrefix,
  onResume,
  onShowFull,
  promptBox,
  submitting,
  voice,
  voiceEnabled,
  voiceError,
  voiceStatus,
}) => {
  const [modeKey, setModeKey] = useState(null);
  const [whereKey, setWhereKey] = useState(null);
  const mode = modeKey ? MODES[modeKey] : null;
  const chosen = mode && whereKey ? mode.options.find((option) => option.key === whereKey) : null;

  // What the choices say goes in front of the prompt, so the agent knows what was asked.
  useEffect(() => { onPrefix(chosen?.prefix || ''); }, [onPrefix, chosen]);
  useEffect(() => () => onPrefix(''), [onPrefix]);
  // Which of the three was chosen, for the app it leads to (Home reports it with the prompt).
  useEffect(() => { onMode?.(modeKey); }, [onMode, modeKey]);

  const choose = (key) => {
    setModeKey(key);
    setWhereKey(MODES[key].options[0].key);
  };
  const back = () => { setModeKey(null); setWhereKey(null); };

  // On the first screen a letter picks: F for Ask & find, C for Create, A for Build an app. Typing somewhere, or a shortcut of
  // the browser (Cmd+F, Ctrl+A), is left alone.
  useEffect(() => {
    if (mode) { return undefined; }
    const onKey = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) { return; }
      const target = event.target;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) { return; }
      const pressed = String(event.key).toLowerCase();
      if (voiceEnabled && pressed === TELL_SHORTCUT.toLowerCase()) {
        event.preventDefault();
        voice.toggle();
        return;
      }
      const entry = Object.entries(MODES).find(([, item]) => item.shortcut.toLowerCase() === pressed);
      if (entry) {
        event.preventDefault();
        choose(entry[0]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode]);

  // Esc goes back, as the page says.
  useEffect(() => {
    if (!mode) { return undefined; }
    const onKey = (event) => { if (event.key === 'Escape') { back(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, voiceEnabled, voice.toggle]);

  const pickOption = (option) => {
    setWhereKey(option.key);
    if (option.attach) { onAttach(); } else { onFocusPrompt(); }
  };

  // The question is the next thing to do: the cursor is in the prompt box as soon as the second step opens, by click or by key.
  useEffect(() => {
    if (!modeKey) { return undefined; }
    const frame = requestAnimationFrame(() => onFocusPrompt());
    return () => cancelAnimationFrame(frame);
  }, [modeKey]);

  const current = submitting ? 3 : mode ? 2 : 1;

  if (!mode) {
    return (
      <div className="hm hs">
        <header className="hm-head">
          <div className="hm-head-text">
            <h1 className="hm-title">{firstName ? `What are we doing today, ${firstName}?` : 'What are we doing today?'}</h1>
            <p className="hm-subtitle">Pick what you want to do and Flow takes it from there.</p>
          </div>
          {continueSession && (
            <div className="hm-continue">
              <span aria-hidden="true" className="hm-continue-icon"><RefreshIcon size={20} /></span>
              <div className="hm-continue-text">
                <strong>Continue</strong>
                <span>{continueSession.name} · {formatRelativeTime(continueSession.updatedAt)}</span>
              </div>
              <button className="hm-resume" onClick={onResume} type="button">Resume <ArrowIcon size={16} /></button>
            </div>
          )}
        </header>
        <Stepper current={current} />
        <div className="hs-choices">
          {Object.entries(MODES).map(([key, item]) => (
            <button aria-keyshortcuts={item.shortcut} className="hs-choice" key={key} onClick={() => choose(key)} type="button">
              <span className="hs-choice-icon"><item.Icon size={18} /></span>
              <span className="hs-choice-title">{item.title}</span>
              <kbd aria-hidden="true" className="hs-key" title={`Press ${item.shortcut}`}>{item.shortcut}</kbd>
              <span className="hs-choice-blurb">{item.blurb}</span>
            </button>
          ))}
        </div>
        {liveNow && <div className="hs-live">{liveNow}</div>}
        <div className="hs-actions">
          {voiceEnabled && (
            <button aria-keyshortcuts={TELL_SHORTCUT} className="hm-resume hs-tell" onClick={voice.toggle} type="button">
              <MicIcon size={16} /> {voice.phase === 'off' ? 'Or just tell me' : 'Stop listening'}
              <kbd aria-hidden="true" className="hs-key hs-key-inline" title={`Press ${TELL_SHORTCUT}`}>{TELL_SHORTCUT}</kbd>
            </button>
          )}
          <span className="hs-tucked">
            Your recent work is tucked away.{' '}
            <button className="hm-see-all" onClick={onShowFull} type="button">Show the full home</button>
          </span>
        </div>
        {voiceError && <div className="hm-voice-error" role="alert">{voiceError}</div>}
        {voiceStatus && (
          <div aria-live="polite" className="hm-voice-status" role="status">
            <VoiceWave level={voice.level} mode={voice.phase === 'hearing' ? 'live' : 'idle'} />
            <span>{voiceStatus}</span>
          </div>
        )}
        <footer className="home-footer">
          <p>Made by Mia-Platform with ❤️ :-)</p>
        </footer>
      </div>
    );
  }

  return (
    <div className="hm hs">
      <Stepper current={current} />
      <div className="hs-bar">
        <button className="hs-pillbtn" onClick={back} type="button"><BackIcon size={14} /> Back</button>
        <span className="hs-pillbtn hs-pill-on"><mode.Icon size={14} /> {mode.title}</span>
        <span className="hs-esc">Esc to go back</span>
      </div>
      <h1 className="hm-title">{mode.heading}</h1>
      {promptBox({placeholder: mode.placeholder, label: mode.heading})}
      <div className="hm-chips" role="group" aria-label="Choose">
        {mode.options.map((option) => (
          <button
            aria-pressed={option.key === whereKey}
            className={`hm-chip${option.key === whereKey ? ' hs-chip-on' : ''}`}
            key={option.key}
            onClick={() => pickOption(option)}
            type="button"
          >
            <option.Icon size={14} /> {option.label}
          </button>
        ))}
      </div>
      <p className="hs-next"><strong>Next</strong> · {mode.next}</p>
      <footer className="home-footer">
        <p>Made by Mia-Platform with ❤️ :-)</p>
      </footer>
    </div>
  );
};

HomeSimple.propTypes = {
  continueSession: PropTypes.object,
  firstName: PropTypes.string,
  formatRelativeTime: PropTypes.func.isRequired,
  liveNow: PropTypes.node,
  onAttach: PropTypes.func.isRequired,
  onFocusPrompt: PropTypes.func.isRequired,
  onMode: PropTypes.func,
  onPrefix: PropTypes.func.isRequired,
  onResume: PropTypes.func.isRequired,
  onShowFull: PropTypes.func.isRequired,
  promptBox: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  voice: PropTypes.object.isRequired,
  voiceEnabled: PropTypes.bool,
  voiceError: PropTypes.string,
  voiceStatus: PropTypes.string,
};

export default HomeSimple;
export {MODES};
